import React from 'react';
import Portal from '../Portal';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface DownloadProgressModalProps {
  isVisible: boolean;
  current: number;
  total: number;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 2.5rem;
  border-radius: 16px;
  min-width: 320px;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const Title = styled.h4`
  margin-bottom: 1.5rem;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.025em;
`;

const ProgressInfo = styled.div`
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: #34495e;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 12px;
  background: linear-gradient(90deg, #e9ecef 0%, #f8f9fa 100%);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${(props) => props.progress}%;
  height: 100%;
  background: linear-gradient(90deg, #007aff 0%, #5ac8fa 100%);
  border-radius: 6px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 122, 255, 0.3);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const ProgressText = styled.div`
  font-size: 0.95rem;
  color: #6c757d;
  font-weight: 500;
  margin-top: 0.5rem;
`;

const LoadingDots = styled.div`
  display: inline-block;
  margin-left: 0.5rem;

  &::after {
    content: '';
    animation: dots 1.5s infinite;
  }

  @keyframes dots {
    0%,
    20% {
      content: '';
    }
    40% {
      content: '.';
    }
    60% {
      content: '..';
    }
    80%,
    100% {
      content: '...';
    }
  }
`;

const DownloadProgressModal: React.FC<DownloadProgressModalProps> = ({ isVisible, current, total }) => {
  const { t } = useTranslation();
  if (!isVisible) return null;

  const progressPercentage = total > 0 ? (current / total) * 100 : 0;

  return (
    <Portal openModal>
      <ModalOverlay>
        <ModalContainer>
          <Title>
            {t('이미지 다운로드 중')}
            <LoadingDots />
          </Title>
          <ProgressInfo>
            {current} / {total}
          </ProgressInfo>
          <ProgressBarContainer>
            <ProgressBar progress={progressPercentage} />
          </ProgressBarContainer>
          <ProgressText>
            {Math.round(progressPercentage)}% {t('완료')}
          </ProgressText>
        </ModalContainer>
      </ModalOverlay>
    </Portal>
  );
};

export default DownloadProgressModal;
